import { GAME_SETTINGS } from "app/data/game-settings";
import getWordCorrectness from "app/score/getWordCorrectness";
import { type SpellingData } from "app/score/scoreState";
import Misspelling from "app/ui/Misspelling";
import { WordCompletionIndicator } from "app/ui/WordCompletionIndicator";
import cn from "app/utils/classnames";
import { WordStatus } from "app/utils/wordStatus";
import { useEffect, useRef } from "react";

const HEADER_PADDING = "px-1 py-0.5";
const HEADER_HEIGHT = "h-5.5";
const DIVIDER = "border-l border-l-gray-200";
const ROW_PADDING = "pl-1";
const NUM_COL_W = "w-14";
const WORD_COL_FLEX = "flex-3";
const SPELLING_COL_FLEX = "flex-4";
const POINTS_COL_FLEX = "shrink basis-14";

interface WordSpellingRecordProps {
  numTotalWords: number;
  completedSpellings: Array<SpellingData>;
  className?: string;
  hideCorrectSpelling?: boolean;
  hidePlayerSpelling?: boolean;
  hideScore?: boolean;
  showCorrectness?: boolean;
  suppressCurrent?: boolean;
}

// Visual element that displays the player's spelling of a word alongside its
// correct spelling, and a visual element indicating whether it's correct.
export default function WordSpellingRecord({
  numTotalWords,
  completedSpellings,
  className,
  hideCorrectSpelling = false,
  hidePlayerSpelling = false,
  hideScore = false,
  showCorrectness = false,
  suppressCurrent = false,
}: WordSpellingRecordProps) {
  const scrollContainerRef = useRef<HTMLUListElement>(null);
  const currentWordIndex = completedSpellings ? completedSpellings.length : 0;

  useEffect(() => {
    if (!scrollContainerRef.current) {
      return;
    }

    const scrollTarget = scrollContainerRef.current.children[currentWordIndex];
    if (scrollTarget) {
      scrollTarget.scrollIntoView({
        behavior: "smooth",
        inline: "nearest",
        block: "nearest",
      });
    }
  }, [currentWordIndex]);

  return (
    <div className={cn("w-full", "flex flex-col", "relative", className)}>
      <div // HEADER
        className={cn(
          "flex",
          "text-left align-top text-xs uppercase",
          "border border-gray-200",
          HEADER_HEIGHT,
        )}
      >
        <div // EMPTY CELL ABOVE NUMBERS
          className={cn("bg-gray-200", NUM_COL_W)}
        />
        {!hideCorrectSpelling && (
          <p
            className={cn(
              "outline-l outline-l-gray-300",
              WORD_COL_FLEX,
              HEADER_PADDING,
              HEADER_HEIGHT,
              DIVIDER,
            )}
          >
            Word
          </p>
        )}
        {!hidePlayerSpelling && (
          <p
            className={cn(
              SPELLING_COL_FLEX,
              HEADER_PADDING,
              HEADER_HEIGHT,
              DIVIDER,
            )}
          >
            Your spelling
          </p>
        )}
        {!hideScore && (
          <p
            className={cn(
              POINTS_COL_FLEX,
              HEADER_PADDING,
              HEADER_HEIGHT,
              DIVIDER,
            )}
          >
            Points
          </p>
        )}
        {showCorrectness && (
          <p
            className={cn(
              POINTS_COL_FLEX,
              HEADER_PADDING,
              HEADER_HEIGHT,
              DIVIDER,
            )}
          >
            Accuracy
          </p>
        )}
      </div>
      <ul // DATA ROWS
        ref={scrollContainerRef}
        className={cn("overflow-y-scroll")}
      >
        {createWordList(
          numTotalWords,
          completedSpellings,
          hideCorrectSpelling,
          hidePlayerSpelling,
          hideScore,
          showCorrectness,
          suppressCurrent,
        ).map((li) => {
          return li;
        })}
      </ul>
      <div // INNER SHADOW
        className={cn(
          "absolute top-5.5 bottom-0 left-0 right-0",
          "shadow-[inset_0px_0px_4px_rgba(0,0,0,0.1)]",
          "overflow-clip",
          "pointer-events-none",
        )}
      />
    </div>
  );
}

// Returns array of row elements to be added to the scrollable list.
function createWordList(
  numTotalWords: number,
  completedSpellings: Array<SpellingData>,
  hideCorrectSpelling: boolean = false,
  hidePlayerSpelling: boolean = false,
  hideScore: boolean = false,
  showCorrectness: boolean = false,
  suppressCurrent: boolean = false,
): Array<React.ReactNode> {
  const listElements = [];

  for (let i = 0; i < numTotalWords; i++) {
    const wordIndex = i;
    let wordStatus = getWordStatus(i, completedSpellings);
    if (suppressCurrent && wordStatus === WordStatus.CURRENT) {
      wordStatus = WordStatus.UPCOMING;
    }
    const isSpelled =
      wordStatus === WordStatus.CORRECT || wordStatus === WordStatus.INCORRECT;
    // Get the player's spelling if they've already completed this word
    const spellingData = completedSpellings?.[wordIndex] || undefined;
    const playerSpelling = spellingData ? spellingData.userSpelling : "";
    // Alternate bg color for each row
    const bgColor = wordIndex % 2 !== 0 ? "bg-white" : "bg-gray-100";
    const match = spellingData
      ? getWordCorrectness(playerSpelling, spellingData.correctSpelling)
      : 1;

    // Identify words that are partially correct so we can color them
    // differently.
    if (
      wordStatus === WordStatus.INCORRECT &&
      match >= GAME_SETTINGS.matchCorrectThreshold
    ) {
      wordStatus = WordStatus.PARTIALLY_CORRECT;
    }

    listElements.push(
      <li className={cn("w-full h-8", "scroll-m-1.5", bgColor)} key={wordIndex}>
        <div // CONTENT CONTAINER
          className="h-full flex items-center"
        >
          <div // COL FOR COMPLETION INDICATOR & WORD NUMBER
            className={cn(
              "flex gap-1 items-center",
              "pl-1",
              ROW_PADDING,
              NUM_COL_W,
            )}
          >
            {/* <div // COMPLETION INDICATOR
            className={cn(
              "self-center flex flex-col justify-end pl-1",
              ROW_PADDING
            )}
          > */}
            <WordCompletionIndicator status={wordStatus}>
              {wordIndex + 1}
            </WordCompletionIndicator>
            <div // WORD NUMBER
              className={cn("w-4 flex items-center", ROW_PADDING)}
            >
              {`${wordIndex + 1}.`}
            </div>
          </div>
          <div // CORRECT SPELLING
            className={cn(
              "h-full",
              "overflow-x-scroll scrollbar-hidden",
              "flex items-center",
              hideCorrectSpelling && "hidden",
              WORD_COL_FLEX,
              ROW_PADDING,
              DIVIDER,
            )}
          >
            <p>
              {isSpelled ? completedSpellings[i].correctSpelling : "???"}
            </p>
          </div>
          {!hidePlayerSpelling && (
            <div // PLAYER'S SPELLING
              className={cn(
                "h-full",
                "text-left",
                "overflow-x-scroll scrollbar-hidden",
                SPELLING_COL_FLEX,
                ROW_PADDING,
                DIVIDER,
              )}
            >
              {isSpelled && (
                <div className="h-full flex items-center">
                  {wordStatus === WordStatus.INCORRECT ||
                  wordStatus === WordStatus.PARTIALLY_CORRECT ? (
                    <Misspelling
                      className={
                        wordStatus === WordStatus.PARTIALLY_CORRECT
                          ? "decoration-partial"
                          : ""
                      }
                    >
                      {playerSpelling.toUpperCase()}
                    </Misspelling>
                  ) : (
                    <p className="">{playerSpelling.toUpperCase()}</p>
                  )}
                </div>
              )}
            </div>
          )}
          {isSpelled && showCorrectness && (
            <div // MATCH PERCENTAGE
              className={cn(
                "shrink basis-19 flex text-sm",
                ROW_PADDING,
                DIVIDER,
              )}
            >
              {match >= GAME_SETTINGS.matchCorrectThreshold
                ? `${Math.floor(match * 100)}%`
                : "0%"}
            </div>
          )}
          {!hideScore && (
            <>
              <div // POINTS EARNED
                className={cn(
                  "h-full",
                  "flex justify-center items-center text-sm pr-1",
                  POINTS_COL_FLEX,
                  ROW_PADDING,
                  DIVIDER,
                )}
              >
                {isSpelled && (
                  <>
                    <p>{spellingData.pointValue}</p>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </li>,
    );
  }
  return listElements;
}

// Helper method to get the status of a specified word
function getWordStatus(
  wordIndex: number,
  spellings: Array<SpellingData>,
): WordStatus {
  const numSpelledWords = spellings ? spellings.length : 0;
  if (wordIndex === numSpelledWords) {
    return WordStatus.CURRENT;
  }

  // If the word has already been completed, return the recorded completion
  // state.
  if (spellings?.length > wordIndex) {
    const spellingData = spellings[wordIndex];
    if (spellingData.userSpelling === spellingData.correctSpelling) {
      return WordStatus.CORRECT;
    } else {
      return WordStatus.INCORRECT;
    }
  }

  // Word has not yet come into play
  return WordStatus.UPCOMING;
}
